import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import ErrorIcon from '@material-ui/icons/Error';
import Grow from '@material-ui/core/Grow';
import MaskedInput from 'react-text-mask';
import classNames from 'classnames';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import { Alert } from '@material-ui/lab';
import { keyEmptyCheck } from 'aurora-utils/utils';
import ADGMImage from '../ADGMImage';
import styles from './styles';

function TextMaskCustom(props) {
  const { inputRef, ...other } = props;

  return (
    <MaskedInput
      {...other}
      ref={ref => {
        inputRef(ref ? ref.inputElement : null);
      }}
      placeholderChar={'\u2000'}
      // pipe={autoCorrectedDatePipe}
      // guide
      // keepCharPositions
    />
  );
}

TextMaskCustom.propTypes = {
  inputRef: PropTypes.func.isRequired,
};

function ADGMTextField(props) {
  const {
    classes,
    error,
    warning,
    mask,
    variant,
    fullWidth,
    startAdornment,
    endAdornment,
    required,
    label,
    style,
    customClasses,
    success,
    isPreFilled,
    min,
    max,
    onChange,
    textArea,
    autoFocus,
    handleInputChange,
    value,
    upperLimit,
    onBlur,
    lowerLimit,
    disabled,
    ...otherProps
  } = props;

  const [date, setDate] = useState();
  const [errors, setErrors] = useState();

  function validateupperLimit() {
    if (lowerLimit) {
      return lowerLimit && moment(date, 'DD/MM/YYYY').isBefore(lowerLimit);
    }
    if (upperLimit) {
      return upperLimit && moment(date, 'DD/MM/YYYY').isAfter(upperLimit);
    }
    if (lowerLimit === null) {
      return moment(date, 'DD/MM/YYYY').isBefore('1900-01-01', 'year');
    }
    return false;
  }

  const handleChange = e => {
    setDate(e.target.value);
    onChange && onChange(e);
  };

  useEffect(() => {
    setErrors(error);
  }, [error]);

  useEffect(() => {
    setDate(value);
  }, [value]);
  useEffect(() => {
    const updatedDate = date;
    if (updatedDate || updatedDate !== value) {
      if (moment(updatedDate, 'DD/MM/YYYY', true).isValid()) {
        if (!validateupperLimit()) {
          setErrors();
          const oneDay = 60 * 60 * 24 * 1000;
          const dateParts = updatedDate.split('/');
          const dateSplit = dateParts[1] + '/' + dateParts[0] + '/' + dateParts[2];
          const propsDate = new Date(moment(dateSplit).format('YYYY-MM-DD'));
          const selectedDay =
            new Date(propsDate) - (new Date(propsDate) % oneDay) + new Date(propsDate).getTimezoneOffset() * 1000 * 60;
          handleInputChange && handleInputChange(selectedDay);
        } else {
          setErrors(keyEmptyCheck('date_not_allowed'));
        }
      } else {
        setErrors(keyEmptyCheck('wrong_date_format'));
      }
    } else {
      setErrors('');
      handleInputChange && handleInputChange(null);
    }
  }, [date]);
  const FieldComponent = useMemo(() => (textArea ? TextareaAutosize : TextField), [textArea]);

  return (
    <div className={classNames(classes.container, customClasses?.container)} style={style}>
      {label && (
        <Typography component='p' className={classNames(classes.label, customClasses?.label)}>
          {label} {required && <span className={classes.textADGMRed}>*</span>}
        </Typography>
      )}
      <FieldComponent
        disabled={disabled}
        variant={variant}
        fullWidth={fullWidth}
        autoFocus={autoFocus}
        className={classNames(classes.field, customClasses?.field, {
          [classes.fieldTextArea]: textArea,
          [classes.errorField]: !textArea && errors,
          [classes.errorFieldTextArea]: textArea && errors,
          [classes.sucessField]: success,
          [classes.preFilledField]: isPreFilled,
          [classes.disabled]: disabled,
        })}
        InputProps={{
          ...(mask && {
            inputComponent: TextMaskCustom,
            inputProps: { mask, disabled, ...otherProps },
          }),
          ...(startAdornment && { startAdornment }),
          ...(endAdornment && { endAdornment }),
          ...(success && {
            endAdornment: <ADGMImage name='sucessTick.svg' alt='success' />,
          }),
          ...(errors && { endAdornment: <ErrorIcon color='error' /> }),
          classes: { input: customClasses?.input },
        }}
        value={date}
        // eslint-disable-next-line no-unused-vars
        onBlur={_e => {
          errors && setDate(moment().format('DD/MM/YYYY'));
        }}
        onChange={handleChange}
        {...otherProps}
      />
      {!isEmpty(errors) && (
        <Grow in={!isEmpty(errors)} timeout={400}>
          <span className={classes.errorMsg}>{errors}</span>
        </Grow>
      )}
      {!isEmpty(warning) && (
        <Grow in={!isEmpty(warning)} timeout={400}>
          <Alert severity='error'>{warning}</Alert>
        </Grow>
      )}
    </div>
  );
}

ADGMTextField.propTypes = {
  classes: PropTypes.object.isRequired,
  style: PropTypes.object,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  mask: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  required: PropTypes.bool,
  success: PropTypes.string,
  warning: PropTypes.string,
  error: PropTypes.string,
  variant: PropTypes.string,
  fullWidth: PropTypes.bool,
  startAdornment: PropTypes.object,
  endAdornment: PropTypes.any,
  isPreFilled: PropTypes.bool,
  textArea: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  onChange: PropTypes.func,
  customClasses: PropTypes.object,
  handleInputChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
  value: PropTypes.string,
  upperLimit: PropTypes.string,
  lowerLimit: PropTypes.string,
  disabled: PropTypes.bool,
};

ADGMTextField.defaultProps = {
  style: {},
  label: null,
  mask: null,
  required: false,
  success: null,
  warning: null,
  error: null,
  variant: 'outlined',
  fullWidth: true,
  startAdornment: null,
  endAdornment: <ADGMImage name='calendorIcon.svg' alt='calendar' />,
  isPreFilled: false,
  textArea: false,
  min: null,
  max: null,
  onChange: null,
  customClasses: null,
  autoFocus: false,
  value: null,
  upperLimit: null,
  lowerLimit: null,
  disabled: false,
};

export default withStyles(styles)(ADGMTextField);
